/**
 * The level title card.
 *
 * Shown for a few seconds when a level loads, over live gameplay — it never
 * blocks and never takes the pointer. The whole reveal is CSS animation with
 * staged delays so it costs nothing per frame: the index settles in, the name
 * resolves out of wide tracking, a hairline rule wipes open beneath it, then
 * the blurb arrives and the lot fades away.
 */

import type { LevelSpec } from '../core/types';
import { add, el, replay } from './dom';

/** Must match the tail of the `ui-card-*` animations in styles.css. */
const CARD_DURATION_MS = 8200;

export class LevelCard {
  readonly el = el('div', 'ui-levelcard');

  private readonly index = el('div', 'ui-levelcard-index');
  private readonly name = el('h2', 'ui-levelcard-name');
  private readonly rule = el('div', 'ui-levelcard-rule');
  private readonly blurb = el('p', 'ui-levelcard-blurb');
  private timer = 0;
  private onDone: (() => void) | null = null;

  constructor() {
    const inner = el('div', 'ui-levelcard-inner');
    add(inner, this.index, this.name, this.rule, this.blurb);
    add(this.el, inner);
    this.el.setAttribute('aria-live', 'polite');
  }

  /** Restart the card for `spec`. Safe to call while one is already playing. */
  play(spec: LevelSpec, onDone: () => void): void {
    this.index.textContent = spec.name;
    this.name.textContent = spec.subtitle;
    this.blurb.textContent = spec.blurb;
    this.onDone = onDone;

    replay(this.el, 'play');
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.finish(), CARD_DURATION_MS);
  }

  /** Cut the card short, e.g. when the run ends underneath it. */
  stop(): void {
    window.clearTimeout(this.timer);
    this.el.classList.remove('play');
    this.onDone = null;
  }

  dispose(): void {
    window.clearTimeout(this.timer);
  }

  private finish(): void {
    this.el.classList.remove('play');
    const done = this.onDone;
    this.onDone = null;
    done?.();
  }
}
